import CACard from '@ds/components/containers/CACard'
import CAHeading from '@ds/components/headers/CAHeading'
import getScriptJson from '@vuejs/utils/getScriptJson'
import {
	computed,
	defineComponent,
	onMounted,
	ref,
} from 'vue'

import { createVueApp } from '@/entrypoints/createVueApp'
import type { Course } from '@/javascript/interfaces'

import type {
	Cell,
	ColumnData,
	RowData,
} from '../components/shared/DataTable'
import MoveDataBetweenTables from '../components/shared/MoveDataBetweenTables'
import { setSelectedRows } from '../components/shared/utils'
import { releasedAtStatusText } from '../components/training_curriculums/utils'
import { PER_PAGE } from '../helpers/_constants'
import type { Certification, Curriculum } from '../services/trainingCurriculums/interfaces'
import { usePackagesAndProgramsStore } from '../stores/PackagesAndProgramsStore'

const ProgramManagementComponent = defineComponent({
	name: 'ProgramManagementPage',

	setup() {
		const element = document.getElementById('program_management_vue_config')
		const config: ProgramManagementConfig = element ? getScriptJson(element.id) : {
			courses: [],
			curriculums: [],
			specialized_certifications: [],
		}

		const cellCertificationDataArray = ref<string[]>([
			'name',
			'pricing_tier',
			'released_at',
		])
		const cellCourseDataArray = ref<string[]>([
			'name',
			'sku',
			'portalName',
		])
		const cellCurriculumDataArray = ref<string[]>([
			'name',
			'payer_source',
		])
		const columnCertifications = ref<ColumnData[]>([
			{
				ariaLabel: 'Sort by Name',
				columnId: 'name',
				isSortable: true,
				name: 'Name',
				width: '300px',
			},
			{
				ariaLabel: 'Sort by Pricing Tier',
				columnId: 'pricing_tier',
				isSortable: true,
				name: 'Tier',
			},
			{
				ariaLabel: 'Sort by Released',
				columnId: 'released_at',
				isSortable: true,
				name: 'Released',
			},
		])
		const columnCourses = ref<ColumnData[]>([
			{
				ariaLabel: 'Sort by Name',
				columnId: 'name',
				isSortable: true,
				name: 'Name',
			},
			{
				ariaLabel: 'Sort by Sku',
				columnId: 'sku',
				isSortable: true,
				name: 'Sku',
			},
			{
				ariaLabel: 'Portal',
				columnId: 'portal',
				isSortable: true,
				name: 'Portal',
			},
		])
		const columnCurriculums = ref<ColumnData[]>([
			{
				ariaLabel: 'Sort by Name',
				columnId: 'name',
				isSortable: true,
				name: 'Name',
				width: '300px',
			},
			{
				ariaLabel: 'Payer Source',
				columnId: 'payer_source',
				isSortable: true,
				name: 'Payer Source',
			},
		])
		const packagesAndProgramsStore = usePackagesAndProgramsStore()
		const selectedCertificationRows = ref<RowData[]>([])
		const selectedCourseRows = ref<RowData[]>([])
		const selectedCurriculumRows = ref<RowData[]>([])

		packagesAndProgramsStore.certifications = config.specialized_certifications
		packagesAndProgramsStore.courses = config.courses
		packagesAndProgramsStore.curriculums = config.curriculums

		const certificationRows = computed(() => config.specialized_certifications.map(c => ({
			cells: dataToCellCertificationDataArray(c),
			rowId: `${c.id}`,
		})))
		const courseRows = computed(() => config.courses.map(c => ({
			cells: dataToCellCourseDataArray(c),
			rowId: `${c.id}`,
		})))
		const curriculumRows = computed(() => config.curriculums.map(c => ({
			cells: dataToCellCurriculumDataArray(c),
			rowId: `${c.id}`,
		})))

		const removeCertificationFromSelectedRows = (data: RowData[]): void => {
			data.forEach(row => {
				const index = selectedCertificationRows.value.findIndex(selectedRow => selectedRow.rowId === row.rowId)

				if (index > -1) {
					selectedCertificationRows.value.splice(index, 1)
				}
			})
		}

		const removeCurriculumFromSelectedRows = (data: RowData[]): void => {
			data.forEach(row => {
				const index = selectedCurriculumRows.value.findIndex(selectedRow => selectedRow.rowId === row.rowId)

				if (index > -1) {
					selectedCurriculumRows.value.splice(index, 1)
				}
			})
		}

		const removeCourseFromSelectedRows = (data: RowData[]): void => {
			data.forEach(row => {
				const index = selectedCourseRows.value.findIndex(selectedRow => selectedRow.rowId === row.rowId)

				if (index > -1) {
					selectedCourseRows.value.splice(index, 1)
				}
			})
		}

		const dataToCellCertificationDataArray = (item: Certification): Cell[] => {
			return [
				{
					title: {
						dataTest: item.name,
						text: item.name,
					},
				},
				{
					badge: {
						style: 'filled',
						text: `${tierText(item.pricing_tier) ?? ''}`,
						type: 'info',
					},
					title: { text: '' },
				},
				{
					badge: {
						style: 'filled',
						text: `${releasedAtStatusText(item.released_at) ?? ''}`,
						type: `${!releasedAtStatusText(item.released_at) ? 'gray' : 'success'}`,
					},
					title: { text: '' },
				},
			]
		}

		const dataToCellCourseDataArray = (item: Course): Cell[] => {
			return [
				{
					title: {
						dataTest: item.name,
						text: item.name,
					},
				},
				{
					badge: {
						style: 'filled',
						text: `${item.sku ?? ''}`,
						type: 'gray',
					},
					title: { text: '' },
				},
				{ title: { text: item.portal_name ?? '' } },
			]
		}

		const dataToCellCurriculumDataArray = (item: Curriculum): Cell[] => {
			return [
				{
					title: {
						dataTest: item.name,
						text: item.name,
					},
				},
				{
					badge: {
						style: 'filled',
						text: `${item.payer_source ?? ''}`,
						type: 'info',
					},
					title: { text: '' },
				},
			]
		}

		const tierText = (tier: string): string => {
			switch (tier) {
				case 'legacy':
					return 'Legacy'

				case 'tier_1':
					return 'T1'

				case 'tier_2':
					return 'T2'

				default:
					return 'T3'
			}
		}

		const updateCertificationSelectedRows = (data: RowData[]): void => {
			selectedCertificationRows.value.push(...data)
		}

		const updateCourseSelectedRows = (data: RowData[]): void => {
			selectedCourseRows.value.push(...data)
		}

		const updateCurriculumSelectedRows = (data: RowData[]): void => {
			selectedCurriculumRows.value.push(...data)
		}

		onMounted(() => {
			selectedCertificationRows.value = setSelectedRows('program_special_certifications', certificationRows.value)
			selectedCourseRows.value = setSelectedRows('program_courses', courseRows.value)
			selectedCurriculumRows.value = setSelectedRows('program_curriculums', curriculumRows.value)
		})

		return {
			cellCertificationDataArray,
			cellCourseDataArray,
			cellCurriculumDataArray,
			certificationRows,
			columnCertifications,
			columnCourses,
			columnCurriculums,
			config,
			courseRows,
			curriculumRows,
			removeCertificationFromSelectedRows,
			removeCourseFromSelectedRows,
			removeCurriculumFromSelectedRows,
			selectedCertificationRows,
			selectedCourseRows,
			selectedCurriculumRows,
			updateCertificationSelectedRows,
			updateCourseSelectedRows,
			updateCurriculumSelectedRows,
		}
	},

	render() {
		return (
			<>
				<div class="my-4">
					<CAHeading
						additionalClasses="mb-3"
						heading={5}
						title="Courses"
					/>

					<MoveDataBetweenTables
						buttonText="courses"
						cellDataArray={this.cellCourseDataArray}
						columns={this.columnCourses}
						data={this.config.courses}
						data-type="courses"
						filterText="Filter Courses"
						inputId="program_courses"
						itemsPerPage={5}
						rows={this.courseRows}
						selectedRows={this.selectedCourseRows}
						onRemove={this.removeCourseFromSelectedRows}
						onSelect={this.updateCourseSelectedRows}
					/>

					<CACard title="Selected courses">
						<ul id="selected_courses" style="height: 100px; overflow-y: scroll;">
							{this.selectedCourseRows.map(row => <li>{row.cells[0].title.text}</li>)}
						</ul>
					</CACard>
				</div>

				<div class="my-4">
					<CAHeading
						additionalClasses="mb-3"
						heading={5}
						title="Curriculums"
					/>

					<MoveDataBetweenTables
						cellDataArray={this.cellCurriculumDataArray}
						columns={this.columnCurriculums}
						data={this.config.curriculums}
						data-type="curriculums"
						filterText="Filter Curriculum"
						inputId="program_curriculums"
						itemsPerPage={PER_PAGE.ITEMS}
						rows={this.curriculumRows}
						selectedRows={this.selectedCurriculumRows}
						onRemove={this.removeCurriculumFromSelectedRows}
						onSelect={this.updateCurriculumSelectedRows}
					/>

					<CACard title="Selected Curriculums">
						<ul id="selected_curriculums" style="height: 100px; overflow-y: scroll;">
							{this.selectedCurriculumRows.map(row => <li>{row.cells[0].title.text}</li>)}
						</ul>
					</CACard>
				</div>

				<div class="my-4">
					<CAHeading
						additionalClasses="mb-3"
						heading={5}
						title="Specialized Certifications"
					/>

					<MoveDataBetweenTables
						cellDataArray={this.cellCertificationDataArray}
						columns={this.columnCertifications}
						data={this.config.specialized_certifications}
						data-type="specialized certifications"
						filterText="Filter Specialized Certifications"
						inputId="program_special_certifications"
						itemsPerPage={PER_PAGE.ITEMS}
						rows={this.certificationRows}
						selectedRows={this.selectedCertificationRows}
						onRemove={this.removeCertificationFromSelectedRows}
						onSelect={this.updateCertificationSelectedRows}
					/>

					<CACard title="Selected Specialized Certifications">
						<ul id="selected_certifications" style="height: 100px; overflow-y: scroll;">
							{this.selectedCertificationRows.map(row => <li>{row.cells[0].title.text}</li>)}
						</ul>
					</CACard>
				</div>
			</>
		)
	},
})

export default function () {
	const app = createVueApp(ProgramManagementComponent)

	app.mount('#programManagementApp')
}

interface ProgramManagementConfig {
	courses: Course[]
	curriculums: Curriculum[]
	specialized_certifications: Certification[]
}
